import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/auth-context';
import { useActiveMandate } from '@/hooks/use-active-mandate';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

interface JournalEntry {
  id: string;
  transaction_id: string;
  date: string;
  description: string;
  debit_account: string;
  credit_account: string;
  amount: number;
  vat_rate: number;
  created_at: string;
  counterparty?: string;
}

export const JournalTab: React.FC = () => {
  const { organization } = useAuth();
  const { mandate } = useActiveMandate();
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchJournalEntries = async () => {
      if (!mandate?.id) return;

      try {
        setLoading(true);
        const pageSize = 20;
        const offset = (currentPage - 1) * pageSize;

        const { data: entries, error } = await (supabase as any)
          .from('journal_entries')
          .select('*')
          .eq('mandate_id', mandate.id)
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);

        if (error) {
          logger.error('Error fetching journal entries', { error, mandateId: mandate.id, currentPage }, 'journal');
          throw new Error(`Failed to fetch journal entries: ${error.message}`);
        }

        const { count, error: countError } = await supabase
          .from('journal_entries')
          .select('*', { count: 'exact', head: true })
          .eq('mandate_id', mandate.id);

        if (countError) {
          logger.error('Error fetching journal entries count', { error: countError, mandateId: mandate.id }, 'journal');
          throw new Error(`Failed to fetch journal entries count: ${countError.message}`);
        }

        const totalEntries = count || 0;
        const calculatedTotalPages = Math.ceil(totalEntries / pageSize);

        setJournalEntries(entries || []);
        setTotalPages(calculatedTotalPages);
      } catch (error) {
        console.error('Error fetching journal entries:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJournalEntries();
  }, [mandate?.id, currentPage]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = journalEntries.filter(entry =>
        entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.counterparty?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.debit_account.toLowerCase().includes(searchTerm.toLowerCase()) ||
        entry.credit_account.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEntries(filtered);
    } else {
      setFilteredEntries(journalEntries);
    }
  }, [searchTerm, journalEntries]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-CH', {
      style: 'currency',
      currency: 'CHF'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-CH');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-center">
            <i className="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-4"></i>
            <p className="text-gray-600">Lade Journal-Einträge...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search and Stats */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold">Gebuchte Einträge</h3>
          <Badge variant="secondary">
            {filteredEntries.length} Einträge
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-64"
          />
          <Button variant="outline" size="sm">
            <i className="fa-solid fa-download mr-2"></i>
            Export
          </Button>
        </div>
      </div>

      {/* Journal Entries */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="fa-solid fa-book text-brand-primary"></i>
            Journal-Einträge
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredEntries.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <i className="fa-solid fa-book-open text-3xl mb-4"></i>
              <p className="text-lg font-medium mb-2">
                {searchTerm ? 'Keine Einträge gefunden' : 'Noch keine Einträge gebucht'}
              </p>
              <p className="text-sm">
                {searchTerm 
                  ? 'Versuchen Sie einen anderen Suchbegriff'
                  : 'Gebuchte Transaktionen werden hier angezeigt'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-sm">{entry.description}</h4>
                      {entry.counterparty && (
                        <p className="text-xs text-gray-600">{entry.counterparty}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-brand-primary">
                        {formatCurrency(entry.amount)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {formatDate(entry.date)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Soll</div>
                      <div className="font-mono">{entry.debit_account}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-1">Haben</div>
                      <div className="font-mono">{entry.credit_account}</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        MwSt {entry.vat_rate}%
                      </Badge>
                      <span className="text-xs text-gray-500">
                        ID: {entry.transaction_id}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Gebucht: {formatDate(entry.created_at)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
